/** @jsxImportSource @emotion/react */
import { useEffect } from 'react'
import moment from 'moment'
import { Order } from 'store/types'
import { useLazyGetNightOwlShippingHistoryQuery } from 'store/services/labOrder'
import { TestSmarterDeviceData } from 'ts-back-end/dist/ts-back-end/src/business/order/dto'

interface IProps {
  order: Order
}

const DeviceTrackingTab = ({ order }: IProps) => {
  const [getShippingHistory, { currentData: shippingHistory }] =
    useLazyGetNightOwlShippingHistoryQuery()
  const [getReturnShippingHistory, { currentData: returnShippingHistory }] =
    useLazyGetNightOwlShippingHistoryQuery()

  const thirdPartyDevice = (order.thirdPartyDevice as TestSmarterDeviceData) || {}
  const welcomeSent = Object.keys(order.mobileInvites || {})?.length > 0
  const activationCode = Object.values(order?.mobileInvites || {})
    ?.sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1))
    ?.pop()?.code
  const { timestamp, email } = order.mobileInvites?.[activationCode] || {}
  const deviceShipped = !!thirdPartyDevice?.trackingNumber
  const returnTrackingNumber = thirdPartyDevice?.returnTrackingNumber
  const isDelivered = shippingHistory?.tracking_status?.status === 'DELIVERED'

  useEffect(() => {
    if (deviceShipped) {
      getShippingHistory({ orderId: Number(order.id), isReturn: false })
    }
  }, [deviceShipped, getShippingHistory, order.id])

  useEffect(() => {
    if (returnTrackingNumber) {
      getReturnShippingHistory({ orderId: Number(order.id), isReturn: true })
    }
  }, [returnTrackingNumber, getReturnShippingHistory, order.id])

  if (!welcomeSent && !deviceShipped) {
    return (
      <div>
        <div className="row px-4">
          <div className="mt-4 d-flex flex-column">
            <div
              className="alert alert-info"
              css={{
                fontSize: 16,
                lineHeight: 1.6,
              }}
              role="alert">
              <p>
                <strong>TestSmarter team is scheduling the patient.</strong> Please wait for device
                and tracking information.
              </p>
              <p>Check the scheduling notes for updates on this order.</p>
              <p>Please send us a direct message with any questions.</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container ">
      <div className="row px-4">
        {welcomeSent && (
          <div>
            <div className="mt-4 d-flex">
              <i className="fa fa-check text-success mr-2" css={{ fontSize: '1.5rem' }} />
              <h5>Welcome email has been sent to the patient</h5>
            </div>
            <div css={{ marginLeft: '2.5rem' }}>
              {moment(timestamp).format('lll')}: Sent to {email}
            </div>
          </div>
        )}
      </div>

      {deviceShipped && (
        <div className="row px-4">
          <div className="mt-4 d-flex w-100">
            <i className="fa fa-check text-success mr-2" css={{ fontSize: '1.5rem' }} />
            <h5>Device Shipped to Patient</h5>
          </div>
          <div className="mt-1 d-flex w-100" css={{ marginLeft: '2.5rem' }}>
            <div className="d-flex flex-column w-100">
              <div className="mb-2">
                <strong className="mr-2">TestSmarter Device Serial #:</strong>
                {thirdPartyDevice?.serial}
              </div>
              <div className="mb-2">
                <strong className="mr-2">Tracking Number:</strong>
                {thirdPartyDevice?.trackingNumber}{' '}
                <a
                  className="ml-2"
                  href={thirdPartyDevice?.trackingUrlProvider}
                  target="_blank"
                  rel="noreferrer">
                  Track Online
                </a>
              </div>
              {returnTrackingNumber && (
                <div className="mb-2">
                  <strong className="mr-2">Return Tracking Number:</strong>
                  {returnTrackingNumber}{' '}
                  <a
                    className="ml-2"
                    href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${returnTrackingNumber}`}
                    target="_blank"
                    rel="noreferrer">
                    Track Online
                  </a>
                </div>
              )}
            </div>
          </div>
          <br />
          <br />
          <div className="row">
            <div className="col">
              <h5>Shipping Updates (Outbound)</h5>
              {!!shippingHistory?.tracking_history ? (
                <ul css={{ listStyle: 'disc' }}>
                  {shippingHistory?.tracking_history?.map((item: any, index: number) => (
                    <li key={index}>
                      <strong css={{ minWidth: 100 }}>
                        {moment(item.status_date).format('MM/DD/YYYY hh:mm a')}
                      </strong>
                      : {item.status_details} ({item?.location?.city}
                      {item?.location?.state ? `, ${item?.location?.state}` : ''})
                    </li>
                  ))}
                </ul>
              ) : (
                <div>Tracking information will be displayed below as it becomes available.</div>
              )}
            </div>

            {returnTrackingNumber && returnShippingHistory?.tracking_history && (
              <div className="col">
                <div className="d-flex">
                  <h5>Return Tracking Updates</h5>
                </div>
                <ul css={{ listStyle: 'disc', marginTop: '1rem' }}>
                  {returnShippingHistory?.tracking_history?.map((item: any, index: number) => (
                    <li key={index}>
                      <strong css={{ minWidth: 100 }}>
                        {moment(item.status_date).format('MM/DD/YYYY hh:mm a')}
                      </strong>
                      : {item.status_details} ({item?.location?.city}
                      {item?.location?.state ? `, ${item?.location?.state}` : ''})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {isDelivered && (
            <div className="my-4">
              <div className="d-flex">
                <i className="fa fa-check text-success mr-2" css={{ fontSize: '1.5rem' }} />
                <h5>Patient Testing</h5>
              </div>
              <div css={{ marginLeft: '2.5rem', marginTop: '0.5rem' }}>
                The patient has received the device and testing is in progress.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DeviceTrackingTab

